import classNames from 'classnames';
import React, {useCallback, useMemo, useState} from 'react';
import SearchIcon from '../../UI/icons/Search';
import TextInput from '../../UI/TextInput';
import './styles.css';

const CardRadio = ({
  options,
  selected,
  onChange,
  notFoundText,
  searchPlaceholder = 'Search',
  searchHelp,
  loading,
  onSearch,
  multiple,
  groupSelect,
  showIcon = true,
  disclaimer,
  columns = 5,
  beforeList,
}) => {
  const [search, setSearch] = useState('');

  const selectedOption = useMemo(() => {
    if (multiple && selected && !Array.isArray(selected)) {
      return [selected];
    }

    return selected;
  }, [selected, multiple]);

  const filteredOptions = useMemo(() => {
    if (!search.trim()) {
      return options;
    }

    return options
      .map(group => {
        const newGroup = { ...group };
        newGroup.options = newGroup.options.filter(op => {
          return (
            (op.name &&
              op.name.toLowerCase().indexOf(search.toLowerCase()) !== -1) ||
            (op.shortName &&
              op.shortName.toLowerCase().indexOf(search.toLowerCase()) !==
                -1) ||
            (op.keywords &&
              op.keywords.toLowerCase().indexOf(search.toLowerCase()) !== -1)
          );
        });

        return newGroup;
      })
      .filter(group => group.options.length > 0);
  }, [search, options]);

  return (
    <div className={'card-radio'}>
      <div className={'card-radio__header'}>
        <TextInput
          loading={loading}
          type={'text'}
          value={search}
          icon={SearchIcon}
          placeholder={searchPlaceholder}
          onChange={e => {
            setSearch(e.target.value);

            if (!!onSearch) {
              onSearch(e.target.value);
            }
          }}
          help={searchHelp}
          showHelpTooltip={false}
          size={'l'}
        />
      </div>
      {!!disclaimer && (
        <div className={'card-radio__disclaimer'}>{disclaimer}</div>
      )}
      <div className={'card-radio__groups'}>
        {loading && (
          <div className={'card-radio__not-found'}>Loading...</div>
        )}
        {!loading && filteredOptions.length === 0 && !!notFoundText && (
          <div className={'card-radio__not-found'}>{notFoundText}</div>
        )}
        <div className={'select-pane__before-list'}>
          {!!beforeList &&
            typeof beforeList === 'function' &&
            beforeList(selectedOption)}
        </div>
        {filteredOptions.map((group, index) => {
          const isAllSelected =
            selectedOption &&
            Array.isArray(selectedOption) &&
            selectedOption.length > 0
              ? group.options.every(option => {
                  return selectedOption.some(s => s.value === option.value);
                })
              : false;

          return (
            <div key={index} className={'card-radio__group'}>
              {!!group.name && (
                <div className={'card-radio__group-label'}>
                  {group.name}
                  {multiple && group.name !== 'Other' && groupSelect && (
                    <button
                      type={'button'}
                      className={'card-radio__group-select'}
                      onClick={e => {
                        e.preventDefault();
                        e.stopPropagation();

                        if (
                          multiple &&
                          group.name !== 'Other' &&
                          group.options.length > 0
                        ) {
                          if (isAllSelected) {
                            onChange(
                              selectedOption.filter(
                                s =>
                                  !group.options.some(
                                    option => option.value === s.value
                                  )
                              )
                            );
                          } else {
                            onChange([
                              ...selectedOption.filter(
                                s =>
                                  !group.options.some(
                                    option => option.value === s.value
                                  )
                              ),
                              ...group.options,
                            ]);
                          }
                        }
                      }}
                    >
                      {isAllSelected ? 'deselect all' : 'select all'}
                    </button>
                  )}
                </div>
              )}
              <div
                className={'card-radio__options'}
                style={{
                  '--columns': columns,
                }}
              >
                {group.options.map((option, i) => (
                  <Item
                    key={option.value || i}
                    option={option}
                    selected={selectedOption}
                    multiple={multiple}
                    onChange={onChange}
                    showIcon={showIcon}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const Item = ({ selected, option, onChange, multiple, showIcon }) => {
  const checked = useMemo(() => {
    if (!selected) {
      return false;
    }

    if (multiple) {
      return selected.some(s => s.value === option.value);
    }

    return selected.value === option.value;
  }, [selected, option, multiple]);

  const onOptionChange = useCallback(
    v => {
      if (multiple) {
        if (checked) {
          onChange(selected.filter(s => s.value !== v.value));
        } else {
          onChange([...selected, v]);
        }
      } else {
        onChange(v);
      }
    },
    [multiple, checked, selected, onChange]
  );

  return (
    <label
      className={classNames('card-radio__option', {
        'card-radio__option--selected': checked,
      })}
      key={option.value}
    >
      <input
        type={multiple ? 'checkbox' : 'radio'}
        name={'school'}
        value={option.value}
        checked={checked}
        onChange={() => {
          onOptionChange(option);
        }}
      />
      {showIcon && (
        <div className={'card-radio__option-icon'}>
          {option.icon && (
            <img
              width={32}
              height={32}
              src={option.icon}
              alt={option.name}
              loading={'lazy'}
            />
          )}
        </div>
      )}
      <div className={'card-radio__option-title'}>
        {option.shortName || option.name}
      </div>
    </label>
  );
};

export default CardRadio;
